import React, { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Product } from '../data/products';
import ProductCard from './ProductCard';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import './FeaturedProductsSlider.css';

interface FeaturedProductsSliderProps {
    products: Product[];
}

const FeaturedProductsSlider: React.FC<FeaturedProductsSliderProps> = ({ products }) => {
    const { isRTL } = useLanguage();
    const swiperRef = useRef<any>(null);

    const handlePrevSlide = () => {
        if (swiperRef.current) {
            swiperRef.current.slidePrev();
        }
    };

    const handleNextSlide = () => {
        if (swiperRef.current) {
            swiperRef.current.slideNext();
        }
    };

    return (
        <div className={`featured-products-slider ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="slider-container">
                {/* Custom Navigation Buttons */}
                <button
                    className="slider-btn slider-btn-prev"
                    onClick={handlePrevSlide}
                    aria-label="Previous product"
                >
                    <ChevronLeft size={24} className={isRTL ? 'flip' : ''} />
                </button>

                <button
                    className="slider-btn slider-btn-next"
                    onClick={handleNextSlide}
                    aria-label="Next product"
                >
                    <ChevronRight size={24} className={isRTL ? 'flip' : ''} />
                </button>

                <Swiper
                    modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
                    onSwiper={(swiper: SwiperType) => (swiperRef.current = swiper)}
                    spaceBetween={30}
                    slidesPerView={1}
                    centeredSlides={true}
                    loop={true}
                    autoplay={{
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                    }}
                    effect="coverflow"
                    coverflowEffect={{
                        rotate: 20,
                        stretch: 0,
                        depth: 100,
                        modifier: 1,
                        slideShadows: true,
                    }}
                    pagination={{
                        clickable: true,
                        dynamicBullets: true,
                    }}
                    breakpoints={{
                        640: {
                            slidesPerView: 1.5,
                            spaceBetween: 20,
                        },
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 30,
                        },
                        1024: {
                            slidesPerView: 2.5,
                            spaceBetween: 40,
                        },
                        1200: {
                            slidesPerView: 3,
                            spaceBetween: 50,
                        },
                    }}
                    className="featured-swiper"
                    dir={isRTL ? 'rtl' : 'ltr'}
                >
                    {products.map((product) => (
                        <SwiperSlide key={product.id} className="featured-slide">
                            <div className="slide-content">
                                <ProductCard product={product} />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            {/* Progress Bar */}
            <div className="slider-progress">
                <div className="progress-bar"></div>
            </div>
        </div>
    );
};

export default FeaturedProductsSlider;